"use client";

import { useRef } from "react";
import Image from "next/image";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { trackCTAClick } from "@/app/lib/analytics";

const EASE = [0.16, 1, 0.3, 1] as const;

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduced ? 1 : 0.95]);
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -60]);

  return (
    <section
      ref={sectionRef}
      data-section="section-hero"
      className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden pt-24 pb-16 text-center"
      aria-label="Inicio"
    >
      {/* Background — slow ambient drift, single layer */}
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
        <div className="hero-aurora" />
        <div className="ambient-grid" />
        <div className="ambient-noise" />
      </div>

      <motion.div
        style={{ opacity, scale }}
        className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 w-full flex flex-col items-center"
      >
        {/* Big Finbo logo as the centerpiece */}
        <motion.div
          style={{ y: yLogo }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
          className="mb-10 sm:mb-12"
          data-particle-shield
        >
          <Image
            src="/finbo-wordmark.svg"
            alt="Finbo"
            width={490}
            height={153}
            priority
            className="w-[min(78vw,560px)] h-auto"
          />
        </motion.div>

        {/* Headline — big, sans, NO italic */}
        <motion.h1
          style={{ y: yText }}
          className="text-[clamp(28px,4.5vw,56px)] leading-[1.05] tracking-tight font-medium text-white/90 max-w-[20ch] mb-6"
          data-particle-shield
        >
          {"Tomá el control de tu plata.".split(" ").map((word, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.7,
                ease: EASE,
                delay: 0.6 + i * 0.06,
              }}
              className="inline-block mr-[0.25em]"
            >
              {word}
            </motion.span>
          ))}
        </motion.h1>

        <motion.p
          className="text-base sm:text-lg text-white/55 max-w-xl mb-10 leading-relaxed"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.2 }}
          data-particle-shield
        >
          Finbo es la app argentina de control de gastos. Gratis, sin
          publicidad y sin costos ocultos.
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center justify-center gap-3 sm:gap-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 1.4 }}
          data-particle-shield
        >
          <a
            href="#download"
            className="btn-primary"
            onClick={() => trackCTAClick("hero_download", "hero")}
          >
            <Download size={17} />
            Descargar gratis
          </a>
          <a
            href="#capitulos"
            className="btn-outline"
            onClick={() => trackCTAClick("hero_learn_more", "hero")}
          >
            Cómo funciona
            <ArrowDown size={15} />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-7 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-white/30 z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        aria-hidden="true"
      >
        <motion.div
          animate={reduced ? {} : { y: [0, 6, 0] }}
          transition={{ duration: 2.4, repeat: Infinity }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-[11px] tracking-widest uppercase">Scroll</span>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
